import React from 'react'
import { useSelector } from 'react-redux'
import { Trans } from 'react-i18next'
import { find } from 'lodash'
import { isInGeofence } from 'common'
import { Capacitor } from '@capacitor/core'

import { store } from 'store/reducers'
import { createErrorNotification, createNotification } from 'store/actions/notifications'
import Api from 'services/Api'
import { durationInWords, Format, getLocale } from 'services/Dates'
import { TrackAction } from 'services/TrackAction'
import { refreshCounter } from 'store/actions/refresh'

import { t, currentLang } from 'translations'

export const punchCardRequiresComment = punchCard => {
  return (
    (punchCard.flags.isInLate && !punchCard.leave) || (punchCard.flags.isInEarly && !punchCard.leave) || punchCard.flags.isOutEarly || punchCard.flags.isOutLate
  )
}

const getFlagColorAndTime = (punchCard, locale) => {
  // Punch card has no shift assigne, so we can't validate it
  if (!punchCard.shiftId) return ['info']
  // No flags, no issues
  if (!punchCard.flags) return ['success']
  // Map the issue object
  const flags = punchCard.flags
  const arrivalFlagging = punchCard.arrival && !punchCard.leave
  const leaveFlagging = punchCard.arrival && punchCard.leave
  let delay
  let delayText

  // Confirming arrival
  if (arrivalFlagging) {
    // Sets the amout of time if early or late
    if (flags.isInLate || flags.isInEarly) {
      delay = Format(punchCard.arrival, { outFormat: 'distanceRelativeStrict', relativeDate: punchCard.start, locale })
      delayText = flags.isInLate ? 'punch.LATE' : 'punch.EARLY'
    }
    // Sets the alert color
    return [flags.isInLate || flags.isInEarly ? 'warning' : 'success', delay, delayText]
  }

  // Confirming leave
  if (leaveFlagging) {
    // Sets the amout of time late if early or late
    if (flags.isOutEarly || flags.isOutLate) {
      delay = Format(punchCard.leave, { outFormat: 'distanceRelativeStrict', relativeDate: punchCard.end, locale })
      delayText = flags.isOutLate ? 'punch.LATE' : 'punch.EARLY'
    }
    // Sets the alert color
    return [flags.isOutEarly || flags.isOutLate ? 'warning' : 'success', delay, delayText]
  }
}

function setSuccessMessage({ userInfo, result, actionType = 'inOut', info, options = {}, permission }) {
  const lng = currentLang()
  const [type, delay, delayText] = info ? ['info'] : getFlagColorAndTime(result, getLocale(lng))
  const { hasAlreadyPunchOut } = options

  const {
    settings: { timeZone }
  } = store.getState()
  let icon
  let title
  let description

  if (actionType === 'break') {
    icon = 'fas fa-coffee'
    title = t(info.end ? 'punch.notification.BREAK_END_SAVED' : 'punch.notification.BREAK_START_SAVED', { lng })
    description = (
      <Trans t={t} i18nKey={info.end ? 'punch.notification.BREAK_END_SAVED_DESC' : 'punch.notification.BREAK_START_SAVED_DESC'} tOptions={{ lng }}>
        {{ userName: `${userInfo.firstName} ${userInfo.lastName}` }}
        <b />
        <b>{{ time: Format(info.end || info.start, { outFormat: 'timeComplete', isUTC: true, timeZone, locale: getLocale(lng) }) }}</b>
      </Trans>
    )
  } else if (actionType === 'transfer') {
    icon = 'fad fa-exchange-alt'
    title = t('punch.notification.TRANSFER_SAVED', { lng })
    description = (
      <Trans t={t} i18nKey="punch.notification.TRANSFER_SAVED_DESC" tOptions={{ lng }}>
        {{ userName: `${userInfo.firstName} ${userInfo.lastName}` }}
        <b />
        <b>{{ time: Format(info.time, { outFormat: 'timeComplete', isUTC: true, timeZone, locale: getLocale(lng) }) }}</b>
      </Trans>
    )
  } else if (result.leave) {
    icon = 'fad fa-sign-out'
    title = t(hasAlreadyPunchOut ? 'punch.notification.DEPARTURE_ALREADY_SAVED' : 'punch.notification.DEPARTURE_SAVED', { lng })
    description = (
      <>
        <Trans t={t} i18nKey="punch.notification.DEPARTURE_SAVED_DESC" tOptions={{ lng }}>
          {{ userName: `${userInfo.firstName} ${userInfo.lastName}` }}
          <b />
          <b>{{ time: Format(result.leave, { outFormat: 'timeComplete', locale: getLocale(lng) }) }}</b>
          <b>{{ workedHours: durationInWords(result.minutes.present) }}</b>
        </Trans>
        {delay && delayText ? (
          <div>
            <Trans t={t} i18nKey="punch.notification.DEPARTURE_INFO" tOptions={{ lng }}>
              <span className={`text-${type}`}>{{ delay }}</span>
              {{ type: t(delayText, { lng }) }}
            </Trans>
          </div>
        ) : null}
        <LocationWarning lng={lng} expectedLocation={result.expectedLocation} punchCoords={result.leaveCoordinates} />
      </>
    )
  } else if (result.arrival) {
    icon = 'fad fa-sign-in'
    title = t('punch.notification.ARRIVAL_SAVED', { lng })
    description = (
      <>
        <Trans t={t} i18nKey="punch.notification.ARRIVAL_SAVED_DESC" tOptions={{ lng }}>
          {{ userName: `${userInfo.firstName} ${userInfo.lastName}` }}
          <b />
          <b>{{ time: Format(result.arrival, { outFormat: 'timeComplete', locale: getLocale(lng) }) }}</b>
        </Trans>
        {delay && delayText ? (
          <div>
            <Trans t={t} i18nKey="punch.notification.ARRIVAL_INFO" tOptions={{ lng }}>
              <span className={`text-${type}`}>{{ delay }}</span>
              {{ type: t(delayText, { lng }) }}
            </Trans>
          </div>
        ) : null}
        <LocationWarning lng={lng} expectedLocation={result.expectedLocation} punchCoords={result.arrivalCoordinates} permission={permission} />
      </>
    )
  } else {
    icon = 'fad fa-sign-in'
    title = t('punch.notification.PUNCH_CREATED', { lng })
    description = t('punch.notification.NO_ARRIVAL_OR_DEPARTURE_SAVED', { lng })
  }

  return createNotification({ description, icon, title, type: `${type}`, rawDescription: true, noDate: true })(store.dispatch)
}

export default {
  // Actions to punch in or out of the shift, in or out of a break and transfer to another position
  punch: async (eventId, userInfo, geoLocation, permission) => {
    // Load or create punch card
    const { success: loadCardSuccess, result: { _id: punchCardId } = {} } = await Api.get(`punch/shifts/${eventId}`)
    if (!loadCardSuccess) return store.dispatch(createErrorNotification())

    // Attempt to punch in or out
    const { success: punchSuccess, result: newPunchCard } = await Api.put(`punch/punch/${punchCardId}/shift`, { geoLocation })
    if (!punchSuccess) return store.dispatch(createErrorNotification())
    store.dispatch(refreshCounter('employeePunchCards'))

    TrackAction('PUNCH_IN_OUT', { type: newPunchCard.leave ? 'out' : 'in' })

    // Send a notification to confirm to the user
    setSuccessMessage({ userInfo, result: newPunchCard, permission })
    return newPunchCard
  },
  addComment: async (punchCardId, comment) => {
    const { success } = await Api.put(`punch/punch/${punchCardId}/comment`, { comment })
    if (!success) return store.dispatch(createErrorNotification())
    store.dispatch(refreshCounter('employeePunchCards'))
  },
  transfer: async (punchCardId, positionId, userInfo, comment) => {
    const { success: transferSuccess, result: newPunchCard } = await Api.post(`punch/punch/${punchCardId}/transfer`, { positionId, comment })
    if (!transferSuccess) return store.dispatch(createErrorNotification())
    store.dispatch(refreshCounter('employeePunchCards'))

    setSuccessMessage({ userInfo, result: newPunchCard, actionType: 'transfer', info: newPunchCard.transfers.pop() })
  },
  transferProject: async (punchCardId, projectId, userInfo, comment) => {
    const { success: transferSuccess, result: newPunchCard } = await Api.post(`punch/punch/${punchCardId}/transfer/project`, { projectId, comment })
    if (!transferSuccess) return store.dispatch(createErrorNotification())
    store.dispatch(refreshCounter('employeePunchCards'))

    setSuccessMessage({ userInfo, result: newPunchCard, actionType: 'transfer', info: newPunchCard.transfers.pop() })
  },
  startBreak: async (punchCardId, selectedBreak, userInfo) => {
    const { success: transferSuccess, result: newPunchCard, _id } = await Api.post(`punch/punch/${punchCardId}/break/start`, { break: selectedBreak })
    if (!transferSuccess) return store.dispatch(createErrorNotification())
    store.dispatch(refreshCounter('employeePunchCards'))

    // Is an unplanned break --> we assign the returned ID to the break
    if (_id) selectedBreak._id = _id

    // Find the break to get its information
    let bInfo = find(newPunchCard.breaks, { _id: selectedBreak._id })

    setSuccessMessage({ userInfo, result: newPunchCard, actionType: 'break', info: bInfo })
  },
  endBreak: async (punchCardId, currentlyPunchedBreak, userInfo) => {
    const { success: transferSuccess, result: newPunchCard } = await Api.post(`punch/punch/${punchCardId}/break/end`, {
      breakId: currentlyPunchedBreak._id
    })
    if (!transferSuccess) return store.dispatch(createErrorNotification())
    store.dispatch(refreshCounter('employeePunchCards'))

    // Find the break to get its information
    let bInfo = find(newPunchCard.breaks, { _id: currentlyPunchedBreak._id })

    setSuccessMessage({ userInfo, result: newPunchCard, actionType: 'break', info: bInfo })
  },
  getFlagColorAndTime,
  setSuccessMessage,
  punchCardRequiresComment
}

const LocationWarning = ({ expectedLocation, punchCoords, lng, permission }) => {
  const {
    settings: { enablePunchGeolocation }
  } = useSelector(state => state)
  const isNative = Capacitor.isNativePlatform()
  const deviceLocationRequired = isNative && enablePunchGeolocation

  if (!deviceLocationRequired) return null

  if (permission === 'denied')
    return (
      <div className="mt-4 rounded border-warning">
        <div className="d-flex flex-row text-warning m-2">
          <div className="mr-2">
            <i className="fa-regular fa-circle-info" />
          </div>
          <div className="font-weight-bold">{t('punch.notification.NO_LOCATION_ACCESS', { lng })}</div>
        </div>
      </div>
    )

  if (isInGeofence(expectedLocation, punchCoords)) return null

  return (
    <div className="mt-4 rounded border-warning">
      <div className="d-flex flex-row text-warning m-2">
        <div className="mr-2">
          <i className="fa-regular fa-circle-info" />
        </div>
        <div className="font-weight-bold">{t('punch.notification.LOCATION_WARNING', { lng })}</div>
      </div>
    </div>
  )
}
